import { PropsWithChildren } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import {
    Button,
    Table,
    TableBody,
    TableCell,
    TableRow,
    Typography,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';

import { ReportContentWrapper } from './ReportContentWrapper';
import { ReportInfoListTemplate } from './ReportInfoListTemplate';
import { ReportInfoTemplate } from './ReportInfoTemplate';

export type ReportPrintingModalProps = {
    title: string;
    firstHeaderTitle: string;
    firstHeaderValues: string[];
    secondHeaderTitle: string;
    secondHeaderValue: string;
    open: boolean;
    onClose: () => void;
    processing: boolean;
};

const PRINTING_STYLES = `
    @media print {
        @page {
            size: A4;
            margin: 0;
            page-break-after: auto;
        }

        html, body {
            width: 210mm;
            height: 297mm;
        }

        title {
            display: none;
        }

        table {
            page-break-after: auto !important;
        }

        thead {
            display: table-row-group !important;
        }

        .print-table-content {
            overflow: hidden;
        }

        .print-content {
            color-adjust: exact !important;
            -webkit-print-color-adjust: exact !important;
        }

        #header, #footer, #nav,
        .print-actions {
            display: none !important;
        }
    }
`;

export const ReportPrintingModal = ({
    title,
    firstHeaderTitle,
    firstHeaderValues,
    secondHeaderTitle,
    secondHeaderValue,
    open,
    onClose,
    children,
    processing
}: PropsWithChildren<ReportPrintingModalProps>) => {
    const handlePrintClick = () => {
        globalThis.print();
    };

    const getReportGenerationDateMessage = (): string => {
        const currentDate = new Date();
        const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };
        const currentDateToPrint = currentDate.toLocaleDateString('en-US', options);
        return `Report generated: ${currentDateToPrint}`;
    };

    return (
        <Dialog open={open}
            onClose={onClose}
            PaperProps={{ sx: { overflowY: 'scroll', minWidth: '1200px' } }}
        >
            <div className='print-actions' style={{ margin: '32px 55px' }}>
                <Table>
                    <TableBody>
                        <TableRow>
                            <TableCell align="left" sx={{ border: 0, padding: 0 }}>
                                <Button
                                    variant="contained"
                                    disabled={processing}
                                    onClick={handlePrintClick}
                                >
                                    Print
                                </Button>
                            </TableCell>
                            <TableCell align="right" sx={{ borderBottom: 0, padding: 0 }}>
                                <IconButton
                                    aria-label="close"
                                    onClick={onClose}
                                    sx={{ padding: 0 }}
                                >
                                    <CloseIcon />
                                </IconButton>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </div>
            <style>{`${PRINTING_STYLES}`}</style>
            <div className='print-content'>
                <ReportContentWrapper
                    inlineStyles={{
                        marginBottom: '20px'
                    }}
                >
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-end'
                    }}>
                        <Typography
                            gutterBottom
                            variant='h4'
                            color='primary.accent'
                            fontWeight='bold'
                        >
                            {title}
                        </Typography>
                        <Typography
                            gutterBottom
                            variant='subtitle1'
                            color='text.secondary'
                            data-testid='report-date-test-id'
                        >
                            {getReportGenerationDateMessage()}
                        </Typography>
                    </div>

                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <ReportInfoListTemplate
                            title={firstHeaderTitle}
                            values={firstHeaderValues}
                        />
                        <ReportInfoTemplate
                            title={secondHeaderTitle}
                            value={secondHeaderValue}
                        />
                    </div>
                </ReportContentWrapper>
                {children}
            </div>
        </Dialog>
    );
};
